import { NO_PREFERENCE_OPTION } from './noPreference';
import type { FloorPlan } from '../containers/ManageFloorPlans/types';

const nBedsTextMap = {
  0: 'Studio',
  1: '1 Bedroom',
};

export const formatFloorPlanBedsBaths = ({
  nBeds = 0,
  nBaths = 0,
  nHalfBaths = 0,
}: {
  nBeds?: number,
  nBaths?: number,
  nHalfBaths?: number,
}) => `${+nBeds}/${+nBaths + +nHalfBaths * 0.5}`;

export const mapFloorPlanOptions = (floorPlans: FloorPlan[]) => {
  const processedFloorPlans = floorPlans
    ?.map((floorPlan) => {
      const container = { value: null, text: null, nBeds: null };

      container.value = floorPlan.id;
      container.text = `${floorPlan?.internalName} ${floorPlan?.marketingName}`;
      container.nBeds = floorPlan?.nBeds;
      container.nBaths = floorPlan?.nBaths;
      container.imageFileName = floorPlan?.imageFileName;
      container.rentableSquareFeet = floorPlan?.rentableSquareFeet;
      container.affordablePrograms =
        floorPlan?.floorPlanAffordablePrograms?.map(
          (program) =>
            program?.propertyAffordableProgram?.masterAffordableProgram?.name,
        );

      return container;
    })
    .sort((a, b) => a.nBeds - b.nBeds);

  return [NO_PREFERENCE_OPTION].concat(processedFloorPlans) ?? [];
};

export type FloorPlanDisplay = {
  name: ?string,
  isNoPreference: boolean,
  linkableId: ?string,
};

const EMPTY_DISPLAY = { name: null, isNoPreference: false, linkableId: null };

/**
 * Which floor plan a prospect or applicant is on, following the Test Household
 * precedence: the assigned unit's floor plan wins, the prospect's own preference
 * is the pre-assignment fallback.
 *
 * `linkableId` is null whenever the display is an empty state — the "No Preference"
 * sentinel is not a FloorPlan row, so it must never be linked.
 */
export const resolveFloorPlanDisplay = ({
  floorPlans,
  unitFloorPlanId,
  preferredFloorPlanId,
}: {
  floorPlans: ?(FloorPlan[]),
  unitFloorPlanId: ?string,
  preferredFloorPlanId: ?string,
}): FloorPlanDisplay => {
  const displayFor = (floorPlan: FloorPlan) => ({
    name: floorPlan.internalName ?? floorPlan.marketingName,
    isNoPreference: false,
    linkableId: floorPlan.id,
  });

  const unitFloorPlan = floorPlans?.find(({ id }) => id === unitFloorPlanId);
  if (unitFloorPlan) return displayFor(unitFloorPlan);

  if (preferredFloorPlanId === NO_PREFERENCE_OPTION.value) {
    return { name: null, isNoPreference: true, linkableId: null };
  }

  const preferredFloorPlan = floorPlans?.find(
    ({ id }) => id === preferredFloorPlanId,
  );
  if (preferredFloorPlan) return displayFor(preferredFloorPlan);

  return EMPTY_DISPLAY;
};

export const getFloorPlanName = ({
  floorPlans,
  preferredFloorPlanId,
}: {
  floorPlans: FloorPlan[],
  preferredFloorPlanId: string,
}) => {
  let floorPlanName = '---';
  const hasNoPreference = NO_PREFERENCE_OPTION.value === preferredFloorPlanId;

  if (hasNoPreference) {
    floorPlanName = NO_PREFERENCE_OPTION.text;
  } else if (preferredFloorPlanId?.length) {
    const selectedFloorPlan = floorPlans?.find(
      ({ id }) => id === preferredFloorPlanId,
    );

    const hasName =
      selectedFloorPlan?.internalName && !isNaN(selectedFloorPlan?.nBeds);
    floorPlanName = hasName
      ? `${selectedFloorPlan?.internalName.toUpperCase()} (${
          nBedsTextMap[selectedFloorPlan?.nBeds] ??
          `${selectedFloorPlan?.nBeds} Bedrooms`
        })`
      : floorPlanName;
  }

  return floorPlanName;
};
